import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    useTheme,
    Container,
    Paper,
    Stack,
    Alert,
    Link,
    CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from "../../theme";
import { cardSurface, iconChip, sectionPadding, EASE } from "../../styles/surfaces";
import { legalContact } from "../legalContact";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

// Same origin in production — CloudFront routes /api/* to the contact Lambda.
// In development, CRA's dev-server proxy (see "proxy" in package.json) forwards
// this to the deployed CloudFront distribution, which adds the origin secret.
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_API_URL || '/api/contact';

const EMPTY_FORM = { firstName: '', lastName: '', email: '', message: '', company: '' };

// Mirrors the server-side rules in infra/contact/index.mjs. This is convenience
// only — the Lambda revalidates everything, since nothing from a browser is trusted.
const EMAIL_RE = /^[^\s@,;:<>()[\]\\"]+@[^\s@.]+(\.[^\s@.]+)+$/;

const validate = ({ firstName, lastName, email, message }) => {
    const errors = {};

    if (!firstName.trim()) errors.firstName = 'First name is required.';
    else if (firstName.trim().length > 50) errors.firstName = 'First name must be 50 characters or fewer.';

    if (!lastName.trim()) errors.lastName = 'Last name is required.';
    else if (lastName.trim().length > 50) errors.lastName = 'Last name must be 50 characters or fewer.';

    if (!email.trim()) errors.email = 'Email is required.';
    else if (email.trim().length > 254) errors.email = 'Email is too long.';
    else if (!EMAIL_RE.test(email.trim())) errors.email = 'Enter a valid email address.';

    const trimmedMessage = message.trim();
    if (trimmedMessage.length < 20) errors.message = 'Please give us at least 20 characters so we can help.';
    else if (trimmedMessage.length > 5000) errors.message = 'Message must be 5000 characters or fewer.';

    return errors;
};

// The outlined-input border overrides were duplicated verbatim on all four fields.
// `fill` is for the Message box, which stretches to the bottom of the card.
// The error state is deliberately left alone so MUI's palette.error styling shows through.
const fieldSx = (theme, { fill = false } = {}) => ({
    ...(fill ? { flex: 1 } : null),
    '& .MuiOutlinedInput-root': {
        ...(fill ? { height: '100%' } : null),
        borderRadius: 2.5,
        backgroundColor: tokens(theme.palette.mode).background[200],
        transition: `background-color .2s ${EASE}`,
    },
    '& .MuiOutlinedInput-root:not(.Mui-error)': {
        '& fieldset': {
            borderColor: theme.palette.divider,
            transition: `border-color .2s ${EASE}`,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
        // A single hairline going blue on focus is easy to miss on a form this size; the
        // ring gives the active field a visible edge without shifting the layout.
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}`,
        },
    },
});

const Contact = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [values, setValues] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | sending | sent | error
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues((previous) => ({ ...previous, [name]: value }));
        // Clear a field's error as soon as the user starts correcting it.
        setErrors((previous) => (previous[name] ? { ...previous, [name]: undefined } : previous));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const found = validate(values);
        if (Object.keys(found).length > 0) {
            setErrors(found);
            return;
        }

        setStatus('sending');
        setErrorMessage('');

        try {
            const response = await fetch(CONTACT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
            });

            if (response.ok) {
                setValues(EMPTY_FORM);
                setErrors({});
                setStatus('sent');
                return;
            }

            if (response.status === 429) {
                setStatus('error');
                setErrorMessage(
                    "You've sent a few messages already. Please wait a few minutes before trying again, "
                    + `or email us directly at ${legalContact.email}.`
                );
                return;
            }

            const data = await response.json().catch(() => null);

            // The server found something our client-side checks let through.
            if (response.status === 400 && data?.fields) {
                setErrors(data.fields);
                setStatus('idle');
                return;
            }

            setStatus('error');
            setErrorMessage(
                `Something went wrong sending your message. Please try again, or email us directly at ${legalContact.email}.`
            );
        } catch {
            setStatus('error');
            setErrorMessage(
                `We couldn't reach the server. Please check your connection and try again, or email us directly at ${legalContact.email}.`
            );
        }
    };

    const contactInfo = [
        {
            icon: <LocationOnIcon />,
            title: "Our Location",
            content: legalContact.addressInline
        },
        {
            icon: <AccessTimeIcon />,
            title: "Working Hours",
            content: "Monday - Friday: 8:00 AM - 5:00 PM\nSaturday: Closed\nSunday: Closed"
        },
        {
            icon: <PhoneIcon />,
            title: "Phone",
            content: legalContact.phone,
            href: legalContact.phoneHref
        },
        {
            icon: <EmailIcon />,
            title: "Email",
            content: legalContact.email,
            href: `mailto:${legalContact.email}`
        }
    ];

    // No minHeight here. This Box sits inside the flex column in App.js, whose middle
    // section is already `flex: 1` — a 100vh floor on top of that meant header + 100vh +
    // footer, so the page always scrolled even when the content was short.
    return (
        <Box sx={{ backgroundColor: colors.background[200], ...sectionPadding }}>
            {/* `lg` rather than `xl`: at xl the two columns ran almost edge to edge on a
                1440 screen, giving the intro paragraph a far longer measure than the same
                copy gets on Home or About. This matches the rest of the site. */}
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Left Column - Contact Information */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ height: '100%' }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontSize: 'clamp(2rem, 1.6rem + 1.8vw, 2.75rem)',
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Get in Touch
                                </Typography>
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{
                                    mb: 4,
                                    maxWidth: '62ch',
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.8
                                }}
                            >
                                We're here to support all your facade installation and remediation needs. Whether you're
                                delivering a new-build development, refurbishment, or remedial project, our experienced
                                team provides professional labour-only installation services for curtain walling, windows,
                                doors, cladding, and associated facade works. We are committed to delivering high-quality
                                workmanship, reliable service, and safe, efficient installations that meet the highest
                                industry standards.
                            </Typography>

                            <Stack spacing={2}>
                                {contactInfo.map((info, index) => (
                                    <Paper
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            ...cardSurface(theme)
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                            <Box sx={iconChip(theme, 48)}>{info.icon}</Box>
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    {info.title}
                                                </Typography>
                                                {info.href ? (
                                                    <Link
                                                        href={info.href}
                                                        variant="body1"
                                                        underline="hover"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            '&:hover': { color: theme.palette.primary.main }
                                                        }}
                                                    >
                                                        {info.content}
                                                    </Link>
                                                ) : (
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            whiteSpace: 'pre-line'
                                                        }}
                                                    >
                                                        {info.content}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Right Column - Contact Form */}
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, md: 4 },
                                ...cardSurface(theme),
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box sx={{ mb: 3.5 }}>
                                {/* `component="h2"`: this is the second heading on the page
                                    and "Get in Touch" is already the h1, so rendering both
                                    as h1 left the document with two top-level headings. */}
                                <Typography
                                    variant="h1"
                                    component="h2"
                                    sx={{
                                        fontSize: 'clamp(1.75rem, 1.45rem + 1.4vw, 2.25rem)',
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Send Us a Message
                                </Typography>
                            </Box>

                            <Stack
                                component="form"
                                onSubmit={handleSubmit}
                                noValidate
                                spacing={3}
                                sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}
                            >
                                {/* Honeypot. Positioned off-screen rather than display:none —
                                    some bots skip hidden fields but still fill positioned ones. */}
                                <Box
                                    component="input"
                                    type="text"
                                    name="company"
                                    value={values.company}
                                    onChange={handleChange}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                                />

                                {status === 'sent' && (
                                    <Alert severity="success" onClose={() => setStatus('idle')}>
                                        Thanks — your message is on its way. We'll get back to you shortly.
                                    </Alert>
                                )}

                                {status === 'error' && (
                                    <Alert severity="error" onClose={() => setStatus('idle')}>
                                        {errorMessage}
                                    </Alert>
                                )}

                                <Box sx={{ width: '100%' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="firstName"
                                                label="First Name"
                                                variant="outlined"
                                                value={values.firstName}
                                                onChange={handleChange}
                                                error={Boolean(errors.firstName)}
                                                helperText={errors.firstName}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="lastName"
                                                label="Last Name"
                                                variant="outlined"
                                                value={values.lastName}
                                                onChange={handleChange}
                                                error={Boolean(errors.lastName)}
                                                helperText={errors.lastName}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                <TextField
                                    required
                                    fullWidth
                                    name="email"
                                    label="Email"
                                    variant="outlined"
                                    type="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email}
                                    sx={fieldSx}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    name="message"
                                    label="Message"
                                    multiline
                                    rows={10}
                                    variant="outlined"
                                    value={values.message}
                                    onChange={handleChange}
                                    error={Boolean(errors.message)}
                                    helperText={errors.message}
                                    sx={(t) => fieldSx(t, { fill: true })}
                                />

                                <Box sx={{ mt: 'auto' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={status === 'sending'}
                                        startIcon={status === 'sending' ? <CircularProgress size={18} color="inherit" /> : null}
                                        sx={{
                                            py: 1.75,
                                            backgroundColor: theme.palette.primary.main,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark
                                            },
                                            fontSize: '1.0625rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {status === 'sending' ? 'Sending…' : 'Send Message'}
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
